import { Banner, Link } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { BILLING_PATHS, useBillingAlertBanner } from '@/features/billing';
import { accountStore } from '@/services/auth/store';
import { billing } from '@/services/billing';
import { BillingSystem } from '@/services/billing/types';
import {
  isOnPaymentFixPage,
  paymentIssueAlert,
  paymentIssueHref,
  paymentIssueTitle,
  type PaymentIssueAlert,
  type PaymentIssueFacts,
} from './payment-issue-alert';

// These tabs show their own payment banner, which states the reason the payment failed.
const TABS_WITH_THEIR_OWN_ALERT: ReadonlySet<string> = new Set([BILLING_PATHS.overview]);

function usePaymentIssueFacts(): PaymentIssueFacts {
  const account = useStore(accountStore);
  const subscription = useStore(billing.subscription);
  // `startDateInFuture` is a getter over these stores, so subscribe to them for it.
  useStore(billing.futureSubscription);
  useStore(billing.billingInfo);
  const { hasPermission } = usePermissions();

  return {
    accountState: account?.account_state,
    isOrdwaySubscription: subscription?.billingSystem === BillingSystem.Ordway,
    isSubscriptionActive: useStore(billing.isSubscriptionActive),
    isPendingCheckout: useStore(billing.isPendingCheckout),
    isLumpSumPlan: useStore(billing.isLumpSumPlan),
    hasAchSetup: useStore(billing.hasAchSetup),
    hasCard: useStore(billing.hasCard),
    hasFailedOverageCharge: useStore(billing.hasFailedOverageCharge),
    lastPaymentError: useStore(billing.lastPaymentError),
    pinnedStartDate: useStore(billing.pinnedStartDate),
    hasPinnedPlan: useStore(billing.pinnedPlan) != null,
    startDateInFuture: billing.startDateInFuture,
    canViewBilling: hasPermission('account/billing/view'),
  };
}

type ShownPaymentIssue = { alert: PaymentIssueAlert; href: string | undefined };

function useShownPaymentIssue(pathname: string): ShownPaymentIssue | null {
  const facts = usePaymentIssueFacts();
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const hasTabAlert = useBillingAlertBanner() != null;

  const alert = paymentIssueAlert(facts);
  if (alert == null) {
    return null;
  }
  const href = paymentIssueHref(alert, {
    cardTab: BILLING_PATHS.billingDetails,
    billingHistory: BILLING_PATHS.billingHistory,
    checkout: BILLING_PATHS.checkout,
    isOrdwayCustomer,
  });
  if (isOnPaymentFixPage(pathname, href, BILLING_PATHS.checkout)) {
    return null;
  }
  if (hasTabAlert && TABS_WITH_THEIR_OWN_ALERT.has(pathname)) {
    return null;
  }
  return { alert, href };
}

function BillingEmailLink() {
  return (
    <Link href="mailto:billing@customer.io" variant="secondary" isExternal={true}>
      billing@customer.io
    </Link>
  );
}

type PaymentIssueDescriptionProps = {
  alert: PaymentIssueAlert;
  href: string;
};

function PaymentIssueDescription({ alert, href }: PaymentIssueDescriptionProps) {
  const isMasqReadOnly = useStore(billing.isMasqReadOnly);
  const formattedStartDate = useStore(billing.formattedStartDate);
  const clickHere = (
    <Link href={href} variant="secondary" isDisabled={isMasqReadOnly}>
      Click here
    </Link>
  );

  switch (alert.kind) {
    case 'payment-error':
      return (
        <>
          {clickHere} to retry or update the card on file or contact <BillingEmailLink /> to discuss.
        </>
      );
    case 'annual-subscription':
      return (
        <>
          {clickHere} to see currently open invoices or contact <BillingEmailLink /> to discuss.
        </>
      );
    case 'overages-failed':
    case 'payment-failed':
      return (
        <>
          {clickHere} {alert.hasCard ? 'to retry or update the card on file' : 'to add a card'} or contact{' '}
          <BillingEmailLink /> to discuss.
        </>
      );
    case 'start-date-passed':
      return (
        <>
          Your subscription started on {formattedStartDate}. {clickHere} to add a card and finish setting up your
          subscription.
        </>
      );
    case 'no-active-subscription':
      return <>{clickHere} to add a card and finish setting up your subscription.</>;
    case 'ask-an-admin':
      return (
        <>
          Ask your account admin to check the status of your payment or contact <BillingEmailLink /> to discuss.
        </>
      );
    default: {
      const _exhaustive: never = alert;
      return String(_exhaustive);
    }
  }
}

type PaymentIssueBannerProps = {
  pathname: string;
};

function PaymentIssueContent({ pathname }: PaymentIssueBannerProps) {
  const shown = useShownPaymentIssue(pathname);
  if (shown == null) {
    return null;
  }

  return (
    <Banner
      type="announcement"
      variant="critical"
      title={paymentIssueTitle(shown.alert)}
      description={<PaymentIssueDescription alert={shown.alert} href={shown.href ?? ''} />}
      data-test-payment-issue-banner
    />
  );
}

/**
 * Tells an account that its payment has a problem, and links a billing viewer to where it gets fixed.
 * It waits for billing, because which problem it states comes from there. It hides on the page it
 * links to, and on a billing tab that shows its own payment banner.
 */
export function PaymentIssueBanner({ pathname }: PaymentIssueBannerProps) {
  const isBillingInitialized = useStore(billing.initialized);
  if (!isBillingInitialized) {
    return null;
  }
  return <PaymentIssueContent pathname={pathname} />;
}
